/**
 * Route registry: the single list of public pages. The client router, the
 * per-route <head>, the pages sitemap and (later) prerender/AEO derive from it.
 * Admin routes live in the client (their path comes from a Vite env var).
 */

export type Section =
  | "hub"
  | "developers"
  | "business"
  | "apps"
  | "blog"
  | "legal"
  | "system";

export type ChangeFreq = "daily" | "weekly" | "monthly" | "yearly";

export interface RouteDef {
  id: string;
  /** Primary path (canonical). Lowercase; ":param" segments allowed. */
  path: string;
  /** Extra paths that render the same page (canonical stays `path`). */
  aliases?: readonly string[];
  section: Section;
  /** Draft routes are only routed when drafts are enabled (previews). */
  status: "live" | "draft";
  /** "page": the page sets its own <head> (e.g. blog posts). */
  head: "registry" | "page";
  robots: "index" | "noindex";
  sitemap: false | { priority: number; changefreq: ChangeFreq };
  /**
   * Rendered to static HTML at build time (scripts/prerender-pages.ts).
   * Blog pages have their own prerender.
   */
  prerender: boolean;
}

export const ROUTES = [
  {
    id: "home",
    path: "/",
    section: "hub",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true, // dist/public/index.html (the template is kept as dist/template.html)
    sitemap: { priority: 1.0, changefreq: "weekly" },
  },
  {
    id: "privacyPolicy",
    path: "/privacy-policy",
    aliases: ["/privacy"], // linked from shipped Gymtero APKs
    section: "legal",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.3, changefreq: "yearly" },
  },
  {
    id: "termsOfUse",
    path: "/terms-of-use",
    aliases: ["/terms"], // linked from shipped Gymtero APKs
    section: "legal",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.3, changefreq: "yearly" },
  },
  {
    id: "cookiePolicy",
    path: "/cookie-policy",
    aliases: ["/cookies"],
    section: "legal",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.3, changefreq: "yearly" },
  },
  {
    id: "developers",
    path: "/developers",
    section: "developers",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
  },
  {
    id: "business",
    path: "/business",
    section: "business",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.9, changefreq: "monthly" },
  },
  {
    id: "apps",
    path: "/apps",
    section: "apps",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
  },
  {
    id: "nutrinima",
    path: "/apps/nutrinima",
    section: "apps",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.5, changefreq: "monthly" },
  },
  {
    id: "jobxtech",
    path: "/apps/jobxtech",
    section: "apps",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.5, changefreq: "monthly" },
  },
  // Must stay before "gymteroDownload" (more specific path first).
  {
    id: "gymteroPrivacy",
    path: "/apps/gymtero/privacy",
    section: "apps",
    // Live with app v2.14.0 (health-data consent, non-personalized ads).
    // Section 5 changes when the app ships trainer-link acceptance (v2.15).
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.3, changefreq: "yearly" },
  },
  {
    id: "gymteroDeleteAccount",
    path: "/apps/gymtero/delete-account", // Play Console account-deletion URL
    section: "apps",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.3, changefreq: "yearly" },
  },
  {
    id: "gymteroDownload",
    path: "/apps/gymtero",
    section: "apps",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: true,
    sitemap: { priority: 0.8, changefreq: "monthly" },
  },
  {
    id: "blogList",
    path: "/blog/",
    section: "blog",
    status: "live",
    head: "registry",
    robots: "index",
    prerender: false,
    sitemap: false, // listed in sitemap-blog.xml
  },
  {
    id: "blogPost",
    path: "/blog/:slug/",
    section: "blog",
    status: "live",
    head: "page",
    robots: "index",
    prerender: false,
    sitemap: false, // listed in sitemap-blog.xml
  },
  {
    id: "notFound",
    path: "/404",
    section: "system",
    status: "live",
    head: "registry",
    robots: "noindex",
    prerender: false,
    sitemap: false,
  },
] as const satisfies readonly RouteDef[];

export type RouteId = (typeof ROUTES)[number]["id"];
export type RegistryRoute = (typeof ROUTES)[number];

export function routePaths(route: RouteDef): string[] {
  return [route.path, ...(route.aliases ?? [])];
}

export function activeRoutes(includeDrafts = false): readonly RouteDef[] {
  return ROUTES.filter(route => includeDrafts || route.status === "live");
}

export function getRoute(id: RouteId): RouteDef {
  return ROUTES.find(route => route.id === id) as RouteDef;
}

/** Live, indexable routes for sitemap-pages.xml (canonical paths only). */
export function sitemapEntries(): {
  path: string;
  priority: number;
  changefreq: ChangeFreq;
}[] {
  return ROUTES.flatMap(route =>
    route.status === "live" && route.robots === "index" && route.sitemap
      ? [{ path: route.path, ...route.sitemap }]
      : []
  );
}
